/**
 * CREATIVE DETONATOR - Centralized API Client
 *
 * All API calls go through this client, which automatically:
 * - Adds authentication headers when available
 * - Handles 401 (unauthorized) responses
 * - Handles 403 (insufficient credits) responses
 * - Provides typed API methods
 * FRONTEND-25: Uses Logger for environment-aware logging
 */

import { getSessionToken, refreshSessionToken } from '../../features/auth/js/client.js';
import { API_BASE } from './endpoints.js';
import { createLogger } from '../core/logger.js';
// REFACTOR-03: Import Events for centralized event emission
import { emit } from '../core/events.js';
import { AUTH_EVENTS } from '../core/event-types.js';

// Phase 1: Import normalization functions from new models
import {
  normalizeIdea,
  normalizeIdeas,
  ideaToDb,
  normalizeSession,
  normalizeSessions,
  normalizeBrand,
  normalizeBrands,
  brandToDb,
  normalizeProduct,
  normalizeProducts,
  productToDb
} from '../core/models/index.js';

// FRONTEND-25: Namespaced logger for API client
const log = createLogger('api');

// Note: API_BASE is now imported from endpoints.js (Single Source of Truth)

// ============================================================================
// BASE REQUEST FUNCTION
// ============================================================================

/**
 * Make an authenticated API request
 * Includes automatic retry with token refresh on 401
 *
 * @param {string} endpoint - API endpoint (e.g., '/api/generate')
 * @param {Object} options - Fetch options
 * @param {boolean} _isRetry - Internal flag to prevent infinite retry loops
 * @returns {Promise<Response>}
 */
export async function apiRequest(endpoint, options = {}, _isRetry = false) {
  // Get auth token if available
  let token = null;
  try {
    token = await getSessionToken();
  } catch (e) {
    // Auth not initialized, continue without token
  }

  // Build headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make request
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });

  // Handle 401 (unauthorized) - try token refresh before redirecting
  if (response.status === 401) {
    // Only retry once to prevent infinite loops
    if (!_isRetry) {
      log.warn('401 received - attempting token refresh...');
      const newToken = await refreshSessionToken();

      if (newToken) {
        log.info('Token refreshed - retrying request');
        // Retry the request with the new token
        return apiRequest(endpoint, options, true);
      }
    }

    // Token refresh failed or this is already a retry - redirect to login
    log.warn('Token refresh failed - redirecting to login');
    sessionStorage.setItem('redirectAfterLogin', window.location.href);
    window.location.href = '/features/auth/pages/login.html';
    throw new Error('Session expired. Please login again.');
  }

  // Handle 402 (payment required) - insufficient credits
  // CREDIT-FIX: Backend sends 402 for insufficient credits, not 403
  if (response.status === 402) {
    const data = await response.json();
    if (data.code === 'INSUFFICIENT_CREDITS') {
      // REFACTOR-03: Use centralized event bus instead of raw CustomEvent
      emit(AUTH_EVENTS.CREDITS_INSUFFICIENT, {
        required: data.required,
        available: data.available
      });
      throw new Error(`Insufficient credits. Need ${data.required}, have ${data.available}`);
    }
    throw new Error(data.error || 'Payment required');
  }

  // Handle 403 (forbidden) - access denied
  if (response.status === 403) {
    const data = await response.json();
    throw new Error(data.error || 'Access denied');
  }

  return response;
}

/**
 * Make a JSON API request and parse response
 *
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>}
 */
export async function apiJson(endpoint, options = {}) {
  const response = await apiRequest(endpoint, options);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `API error: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// API METHODS
// ============================================================================

export const api = {
  // ========== GENERATION ==========

  /**
   * Generate ideas (non-streaming)
   */
  generate: (data) => apiJson('/api/generate', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  /**
   * Generate ideas (streaming)
   * Returns the Response object for SSE handling
   */
  generateStream: (data) => apiRequest('/api/generate-stream', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  /**
   * Refine an idea with Art Director (normalized response)
   */
  refineIdea: async (data) => {
    const raw = await apiJson('/api/refine-idea', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    // Normalize idea in response if present
    if (raw.idea) {
      raw.idea = normalizeIdea(raw.idea);
    }
    return raw;
  },

  /**
   * Generate image for an idea
   */
  generateImage: (data) => apiJson('/api/image-gen/generate', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  /**
   * Edit an existing image
   */
  editImage: (data) => apiJson('/api/image-gen/edit', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  /**
   * Generate video scenes
   */
  generateVideoScenes: (data) => apiJson('/api/generate-video-scenes', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  // ========== USER DATA ==========

  /**
   * Get current user profile
   */
  me: () => apiJson('/api/me'),

  /**
   * Update user profile
   * @param {Object} data - Profile fields to update (first_name, last_name, company, job_title, bio)
   */
  updateProfile: (data) => apiJson('/api/me', {
    method: 'PATCH',
    body: JSON.stringify(data)
  }),

  /**
   * Delete avatar
   */
  deleteAvatar: () => apiJson('/api/me/avatar', {
    method: 'DELETE'
  }),

  /**
   * Get user preferences
   */
  getPreferences: () => apiJson('/api/preferences'),

  /**
   * Update user preferences
   * @param {Object} prefs - Preferences to update (merged with existing)
   */
  updatePreferences: (prefs) => apiJson('/api/preferences', {
    method: 'POST',
    body: JSON.stringify(prefs)
  }),

  /**
   * Export all user data
   */
  exportData: () => apiJson('/api/me/export'),

  /**
   * Delete user account (requires confirmation: "DELETE")
   */
  deleteAccount: () => apiJson('/api/me', {
    method: 'DELETE',
    body: JSON.stringify({ confirmation: 'DELETE' })
  }),

  /**
   * Get user's generation sessions (normalized to camelCase)
   */
  getSessions: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await apiJson(`/api/sessions${query ? '?' + query : ''}`);
    // Normalize sessions array
    if (response.sessions) {
      response.sessions = normalizeSessions(response.sessions);
    } else if (Array.isArray(response)) {
      return normalizeSessions(response);
    }
    return response;
  },

  /**
   * Get a specific session (normalized to camelCase)
   */
  getSession: async (id) => {
    const raw = await apiJson(`/api/sessions/${id}`);
    // Backend returns {success: true, session: {...}} - extract the session
    if (raw.success && raw.session) {
      return { success: true, session: normalizeSession(raw.session) };
    }
    return raw; // Return error response as-is
  },

  /**
   * Get user's saved ideas (normalized to camelCase)
   */
  getIdeas: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await apiJson(`/api/ideas${query ? '?' + query : ''}`);
    // Normalize ideas to consistent camelCase format
    if (response.ideas) {
      response.ideas = normalizeIdeas(response.ideas);
    }
    return response;
  },

  /**
   * Save an idea
   */
  saveIdea: (ideaId) => apiJson('/api/ideas/save', {
    method: 'POST',
    body: JSON.stringify({ ideaId })
  }),

  /**
   * Unsave an idea (uses same endpoint as save with saved=false)
   */
  unsaveIdea: (ideaId) => apiJson('/api/ideas/save', {
    method: 'POST',
    body: JSON.stringify({ ideaId, saved: false })
  }),

  /**
   * Delete an idea
   */
  deleteIdea: (ideaId) => apiJson(`/api/ideas/${ideaId}`, {
    method: 'DELETE'
  }),

  /**
   * Batch delete ideas (deep delete with R2 cleanup)
   * @param {string[]} ideaIds - Array of idea UUIDs to delete
   * @returns {Promise<{success: boolean, deleted?: number, cleanup?: object}>}
   */
  batchDeleteIdeas: (ideaIds) => apiJson('/api/ideas/batch-delete', {
    method: 'POST',
    body: JSON.stringify({ ids: ideaIds })
  }),

  /**
   * Update an idea's content (headline, visual, refinement status, etc.)
   * Accepts normalized camelCase data, converts to snake_case for API
   */
  updateIdea: async (ideaId, data) => {
    // Convert to database format if data has camelCase fields
    const dbData = data.sessionId || data.triggerCode ? ideaToDb(data) : data;
    const raw = await apiJson(`/api/ideas/${ideaId}`, {
      method: 'PUT',
      body: JSON.stringify(dbData)
    });
    // Normalize response
    return raw.idea ? { ...raw, idea: normalizeIdea(raw.idea) } : normalizeIdea(raw);
  },

  /**
   * Create a manual idea (user-entered, not AI-generated)
   * Returns normalized idea
   */
  createManualIdea: async (data) => {
    const raw = await apiJson('/api/ideas/manual', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    // Normalize response
    return raw.idea ? { ...raw, idea: normalizeIdea(raw.idea) } : normalizeIdea(raw);
  },

  /**
   * Upload an image for manual idea entry
   * @param {FormData} formData - FormData with 'image' file
   */
  uploadManualImage: async (formData) => {
    const token = await getSessionToken();
    const response = await fetch(`${API_BASE}/api/upload/manual-image`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Upload failed');
    }
    return response.json();
  },

  /**
   * Get user's generated images
   */
  getImages: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiJson(`/api/images${query ? '?' + query : ''}`);
  },

  /**
   * Save an image (to images table)
   */
  saveImage: (data) => apiJson('/api/images', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  /**
   * Get user's generated videos
   */
  getVideos: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiJson(`/api/videos${query ? '?' + query : ''}`);
  },

  /**
   * Get all videos for a specific idea (from database)
   */
  getIdeaVideos: (ideaId) => apiJson(`/api/ideas/${ideaId}/videos`),

  /**
   * Update an idea with an image URL
   */
  updateIdeaImage: (ideaId, data) => apiJson(`/api/ideas/${ideaId}/image`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),

  // ========== CREDITS ==========

  /**
   * Get current credit balance
   */
  getCredits: () => apiJson('/api/credits'),

  /**
   * Get credit transaction history
   */
  getCreditHistory: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiJson(`/api/credits/history${query ? '?' + query : ''}`);
  },

  // ========== CONFIG ==========

  /**
   * Get backend configuration
   */
  getConfig: () => apiJson('/api/config'),

  /**
   * Get available triggers
   */
  getTriggers: () => apiJson('/api/triggers'),

  /**
   * Get status/health check
   */
  getStatus: () => apiJson('/api/status'),

  // ========== BRANDS ==========

  /**
   * Get user's brands (normalized to camelCase)
   */
  getBrands: async () => {
    const response = await apiJson('/api/brands');
    // Normalize brands array
    if (response.brands) {
      response.brands = normalizeBrands(response.brands);
    } else if (Array.isArray(response)) {
      return normalizeBrands(response);
    }
    return response;
  },

  /**
   * Create a brand (normalized response)
   * Accepts camelCase data, converts to snake_case for API
   */
  createBrand: async (data) => {
    // Convert to database format if data has camelCase fields
    const dbData = data.shellColor || data.visualPrompt ? brandToDb(data) : data;
    const raw = await apiJson('/api/brands', {
      method: 'POST',
      body: JSON.stringify(dbData)
    });
    // Normalize response
    return raw.brand ? { ...raw, brand: normalizeBrand(raw.brand) } : normalizeBrand(raw);
  },

  /**
   * Update a brand (normalized response)
   * Accepts camelCase data, converts to snake_case for API
   */
  updateBrand: async (id, data) => {
    // Convert to database format if data has camelCase fields
    const dbData = data.shellColor || data.visualPrompt ? brandToDb(data) : data;
    const raw = await apiJson(`/api/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dbData)
    });
    // Normalize response
    return raw.brand ? { ...raw, brand: normalizeBrand(raw.brand) } : normalizeBrand(raw);
  },

  /**
   * Delete a brand
   */
  deleteBrand: (id) => apiJson(`/api/brands/${id}`, {
    method: 'DELETE'
  }),

  /**
   * Synthesize brand DNA (generate visual and voice prompts)
   * Returns normalized brand
   */
  synthesizeBrand: async (data) => {
    const raw = await apiJson('/api/brands/synthesize', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    // Normalize response if it contains a brand
    if (raw.brand) {
      raw.brand = normalizeBrand(raw.brand);
    }
    return raw;
  },

  /**
   * Get a single brand by ID (normalized to camelCase)
   */
  getBrand: async (brandId) => {
    const raw = await apiJson(`/api/brands/${brandId}`);
    return normalizeBrand(raw);
  },

  /**
   * Get brand stats
   */
  getBrandStats: (brandId) => apiJson(`/api/brands/${brandId}/stats`),

  // ========== PRODUCTS (Brand Sub-entity) ==========

  /**
   * Get products for a brand (normalized to camelCase)
   */
  getProducts: async (brandId) => {
    const response = await apiJson(`/api/brands/${brandId}/products`);
    // Normalize products array
    if (response.products) {
      response.products = normalizeProducts(response.products);
    } else if (Array.isArray(response)) {
      return normalizeProducts(response);
    }
    return response;
  },

  /**
   * Create a product under a brand (normalized response)
   * Accepts camelCase data, converts to snake_case for API
   */
  createProduct: async (brandId, data) => {
    // Convert to database format if data has camelCase fields
    const dbData = data.brandId || data.imageUrl ? productToDb(data) : data;
    const raw = await apiJson(`/api/brands/${brandId}/products`, {
      method: 'POST',
      body: JSON.stringify(dbData)
    });
    // Normalize response
    return raw.product ? { ...raw, product: normalizeProduct(raw.product) } : normalizeProduct(raw);
  },

  /**
   * Update a product (normalized response)
   * Accepts camelCase data, converts to snake_case for API
   */
  updateProduct: async (brandId, productId, data) => {
    // Convert to database format if data has camelCase fields
    const dbData = data.brandId || data.imageUrl ? productToDb(data) : data;
    const raw = await apiJson(`/api/brands/${brandId}/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(dbData)
    });
    // Normalize response
    return raw.product ? { ...raw, product: normalizeProduct(raw.product) } : normalizeProduct(raw);
  },

  /**
   * Delete a product
   */
  deleteProduct: (brandId, productId) => apiJson(`/api/brands/${brandId}/products/${productId}`, {
    method: 'DELETE'
  }),

  /**
   * Reorder products
   */
  reorderProducts: (brandId, order) => apiJson(`/api/brands/${brandId}/products/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ order })
  }),

  // ========== BRAND ASSETS ==========

  /**
   * Get assets for a brand
   */
  getBrandAssets: (brandId) => apiJson(`/api/brands/${brandId}/assets`),

  /**
   * Upload a brand asset
   * @param {string} brandId - Brand UUID
   * @param {FormData} formData - Form data with file, type, name
   */
  uploadBrandAsset: async (brandId, formData) => {
    // Get auth token
    let token = null;
    try {
      token = await getSessionToken();
    } catch (e) {}

    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Don't set Content-Type - let browser set it with boundary for multipart

    const response = await fetch(`${API_BASE}/api/brands/${brandId}/assets/upload`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Upload failed: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Delete a brand asset
   */
  deleteBrandAsset: (brandId, assetId) => apiJson(`/api/brands/${brandId}/assets/${assetId}`, {
    method: 'DELETE'
  }),

  /**
   * Update a brand asset metadata
   */
  updateBrandAsset: (brandId, assetId, data) => apiJson(`/api/brands/${brandId}/assets/${assetId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),

  // ========== ANALYTICS ==========

  /**
   * Get benefits history for a brand (for autocomplete)
   */
  getBenefitsHistory: (brandId) => apiJson(`/api/brands/${brandId}/benefits`),

  /**
   * Get product usage stats for a brand
   */
  getProductsUsage: (brandId) => apiJson(`/api/brands/${brandId}/products-usage`),

  /**
   * Get trigger usage stats for a brand
   */
  getTriggersUsage: (brandId) => apiJson(`/api/brands/${brandId}/triggers-usage`),

  /**
   * Get format usage stats for a brand
   */
  getFormatsUsage: (brandId) => apiJson(`/api/brands/${brandId}/formats-usage`),

  // ========== EXPORT ==========

  /**
   * Export session data
   */
  exportSession: (sessionId, data = {}) => apiJson('/api/export-session', {
    method: 'POST',
    body: JSON.stringify({ sessionId, ...data })
  }),

  // ========== VERSION HISTORY ==========

  /**
   * Get version history for an idea
   * @param {string} ideaId - Idea UUID
   * @returns {Promise<{success: boolean, versions?: Array}>}
   */
  getIdeaVersions: (ideaId) => apiJson(`/api/ideas/${ideaId}/versions`),

  /**
   * Restore idea to a specific version (normalized response)
   * @param {string} ideaId - Idea UUID
   * @param {number} versionNumber - Version to restore
   * @returns {Promise<{success: boolean, idea?: Object}>}
   */
  restoreIdeaVersion: async (ideaId, versionNumber) => {
    const raw = await apiJson(`/api/ideas/${ideaId}/restore/${versionNumber}`, {
      method: 'POST'
    });
    // Normalize idea in response if present
    if (raw.idea) {
      raw.idea = normalizeIdea(raw.idea);
    }
    return raw;
  },

  /**
   * Delete a specific version from history
   * @param {string} ideaId - Idea UUID
   * @param {number} versionNumber - Version to delete
   * @returns {Promise<{success: boolean}>}
   */
  deleteIdeaVersion: (ideaId, versionNumber) => apiJson(`/api/ideas/${ideaId}/versions/${versionNumber}`, {
    method: 'DELETE'
  }),

  /**
   * Duplicate a version (create new version with same content)
   * @param {string} ideaId - Idea UUID
   * @param {number} versionNumber - Version to duplicate
   * @returns {Promise<{success: boolean, version?: object}>}
   */
  duplicateIdeaVersion: (ideaId, versionNumber) => apiJson(`/api/ideas/${ideaId}/versions/${versionNumber}/duplicate`, {
    method: 'POST'
  })
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export config for reference
export { API_BASE };

// SECURITY: Global window exposure removed to prevent XSS token theft
// If non-module scripts need API access, they should import the module
// or use a controlled interface that doesn't expose auth tokens
